import { FilePreviewPanel } from "@portal/components/features/file-preview";
import { createContext, type ReactNode, useCallback, useContext, useState } from "react";
import { citationParts, type DocumentCitation } from "./assistantCitations";

type OpenCitation = (citation: DocumentCitation) => void;

const CitationPreviewContext = createContext<OpenCitation | undefined>(undefined);

/** Opens a cited document in the preview panel; undefined outside a preview provider. */
export const useOpenCitation = () => useContext(CitationPreviewContext);

const CitationPreviewPanel = ({
	citation,
	onClose,
}: {
	citation: DocumentCitation;
	onClose: () => void;
}) => {
	const { fileName, location } = citationParts(citation.title);
	return (
		<FilePreviewPanel
			fileId={citation.fileId}
			onClose={onClose}
			page={citation.page}
			placement="left"
			resizable
			subtitle={location}
			testId="assistant-citation-preview"
			title={fileName}
		/>
	);
};

/**
 * One preview panel for the whole transcript, so clicking a second citation moves the open panel
 * to that document and page rather than stacking another.
 */
export const AdminAssistantCitationPreview = ({ children }: { children: ReactNode }) => {
	const [citation, setCitation] = useState<DocumentCitation | undefined>(undefined);
	const close = useCallback(() => setCitation(undefined), []);

	return (
		<CitationPreviewContext.Provider value={setCitation}>
			{children}
			{citation !== undefined && <CitationPreviewPanel citation={citation} onClose={close} />}
		</CitationPreviewContext.Provider>
	);
};
